import { useState } from 'react';
import {
  ImageIcon,
  Loader2,
  AlertCircle,
  Star,
  Trash2,
  Plus,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import type { PropertyImage } from '@pms/shared';
import { usePlatformProperties } from '@/hooks/api/usePlatform';
import {
  usePropertyImages,
  useCreatePropertyImage,
  useUpdatePropertyImage,
  useDeletePropertyImage,
  useSetCoverImage,
} from '@/hooks/api/useContent';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

// ── Add-image form ──────────────────────────────────────────────────────────

function AddImageForm({ propertyId }: { propertyId: string }) {
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreatePropertyImage(propertyId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    setError(null);
    create.mutate(
      { url: url.trim(), caption: caption.trim() || undefined },
      {
        onSuccess: () => {
          setUrl('');
          setCaption('');
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="flex-1 min-w-[240px]">
        <label htmlFor="img-url" className="block text-xs font-semibold text-gray-500 mb-1">
          Image URL
        </label>
        <input
          id="img-url"
          type="url"
          required
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>
      <div className="flex-1 min-w-[200px]">
        <label htmlFor="img-caption" className="block text-xs font-semibold text-gray-500 mb-1">
          Caption (optional)
        </label>
        <input
          id="img-caption"
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>
      <Button type="submit" disabled={create.isPending || !url.trim()}>
        {create.isPending ? (
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
        ) : (
          <Plus size={16} aria-hidden="true" />
        )}
        Add
      </Button>
      {error && (
        <p className="w-full text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

// ── Image card ──────────────────────────────────────────────────────────────

function ImageCard({
  image,
  propertyId,
  index,
  total,
}: {
  image: PropertyImage;
  propertyId: string;
  index: number;
  total: number;
}) {
  const setCover = useSetCoverImage(propertyId);
  const remove = useDeletePropertyImage(propertyId);
  const update = useUpdatePropertyImage(propertyId);

  function move(direction: -1 | 1) {
    update.mutate({
      id: image.id,
      patch: { sortOrder: image.sortOrder + direction },
    });
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="aspect-video w-full bg-gray-100">
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        <img
          src={image.url}
          alt={image.caption ?? 'Property image'}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      {image.isCover && (
        <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium text-white">
          <Star size={12} aria-hidden="true" /> Cover
        </span>
      )}
      <div className="p-3">
        <p className="truncate text-sm text-gray-700" title={image.caption ?? ''}>
          {image.caption || <span className="text-gray-400">No caption</span>}
        </p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Move up"
              disabled={index === 0 || update.isPending}
              onClick={() => move(-1)}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
            >
              <ArrowUp size={16} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={index === total - 1 || update.isPending}
              onClick={() => move(1)}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
            >
              <ArrowDown size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="flex items-center gap-1">
            {!image.isCover && (
              <button
                type="button"
                aria-label="Set as cover"
                disabled={setCover.isPending}
                onClick={() => setCover.mutate(image.id)}
                className="rounded-md p-1 text-gray-400 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-40"
              >
                <Star size={16} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              aria-label="Delete image"
              disabled={remove.isPending}
              onClick={() => remove.mutate(image.id)}
              className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
            >
              <Trash2 size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function AdminPropertyImagesPage() {
  const { data: properties, isLoading: propsLoading } = usePlatformProperties();
  const [propertyId, setPropertyId] = useState<string>('');
  const { data: images, isLoading, isError, refetch } = usePropertyImages(
    propertyId || null,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Property Images"
        description="Manage listing images for a property — add by URL, reorder, set cover, or remove."
      />

      <div className="max-w-md">
        <label htmlFor="property-select" className="block text-sm font-semibold text-gray-900 mb-1.5">
          Property
        </label>
        <select
          id="property-select"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          disabled={propsLoading}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">Select a property…</option>
          {(properties ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.city}
            </option>
          ))}
        </select>
      </div>

      {!propertyId ? (
        <EmptyState
          icon={ImageIcon}
          title="No property selected"
          description="Pick a property above to manage its images."
        />
      ) : isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
        </div>
      ) : isError ? (
        <div className="py-16 text-center">
          <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
          <p className="text-sm text-red-700 mb-4">Unable to load images.</p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <AddImageForm propertyId={propertyId} />
          {(images ?? []).length === 0 ? (
            <EmptyState
              icon={ImageIcon}
              title="No images yet"
              description="Add the first image using the form above."
            />
          ) : (
            <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3')}>
              {(images ?? []).map((img, i) => (
                <ImageCard
                  key={img.id}
                  image={img}
                  propertyId={propertyId}
                  index={i}
                  total={images?.length ?? 0}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
